import React from 'react';
import { Clock } from 'lucide-react';

/** Normalize availability slot entries to { time, available, status }. */
export function normalizeSlotGrid(slots = [], slotGrid = null) {
  if (Array.isArray(slotGrid) && slotGrid.length) {
    return slotGrid.map((s) => ({
      time: s.time || s,
      available: s.available !== false && s.status !== 'booked' && s.status !== 'full',
      status: s.status || (s.available === false ? 'booked' : 'open'),
    }));
  }
  return (slots || []).map((t) => ({
    time: typeof t === 'string' ? t : t.time,
    available: typeof t === 'string' ? true : t.available !== false,
    status: typeof t === 'string' ? 'open' : (t.status || 'open'),
  }));
}

/** Time-slot picker — booked / full slots visible but not selectable. */
export default function BookingSlotPicker({ slots, slotGrid, pickedSlot, onPick, primary }) {
  const grid = normalizeSlotGrid(slots, slotGrid);
  if (!grid.length) {
    return <p className="text-xs text-zinc-500 italic mt-3">No slots on this date.</p>;
  }
  const openCount = grid.filter((s) => s.available).length;
  const takenCount = grid.filter((s) => !s.available).length;
  return (
    <div className="mt-4" data-testid="booking-slot-step">
      <div className="flex items-center justify-between mb-2 gap-2">
        <p className="text-[11px] uppercase tracking-wider text-zinc-400 flex items-center gap-1">
          <Clock className="h-3 w-3" /> Choose an open time
        </p>
        <p className="text-[10px] text-zinc-500">
          {openCount} open{takenCount ? ` · ${takenCount} unavailable` : ''}
        </p>
      </div>
      <div className="flex flex-wrap gap-2 mb-2 text-[9px] uppercase tracking-wide text-zinc-500">
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm border border-white/30" /> Open
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="h-2 w-2 rounded-sm bg-zinc-700" /> Taken / Full (not bookable)
        </span>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {grid.map((s) => {
          const picked = pickedSlot === s.time;
          const disabled = !s.available;
          return (
            <button
              key={s.time}
              type="button"
              disabled={disabled}
              onClick={() => { if (!disabled) onPick(s.time); }}
              title={disabled ? (s.status === 'full' ? 'Day capacity full' : 'Already booked') : `Book ${s.time}`}
              aria-disabled={disabled}
              className={`relative px-2.5 py-1.5 rounded-md text-[11px] font-mono border transition ${
                picked ? 'text-white border-transparent' :
                disabled ? 'border-white/10 text-zinc-600 bg-white/[0.03] cursor-not-allowed line-through decoration-zinc-600' :
                'text-zinc-200 border-white/15 hover:bg-white/10'
              }`}
              style={picked ? { background: primary, borderColor: primary } : {}}
              data-testid={`booking-slot-${s.time}`}
              data-status={s.status}
            >
              {s.time}
              {disabled && (
                <span className="absolute -top-1.5 -right-1 text-[8px] uppercase tracking-wide px-1 rounded bg-zinc-700 text-zinc-300 no-underline">
                  {s.status === 'full' ? 'Full' : 'Taken'}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
